import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { MDBContainer, MDBSpinner } from "mdb-react-ui-kit";
import { useAuth } from "../context/AuthContext";
import { normalizeRole } from "../utils/rolePermissions";

import {
  formatMoney,
  formatDisplayOrderNumber,
  formatDateTime,
  periodLabel,
} from "./VendorPayoutsPage";
import "./VendorPayoutReceiptPage.css";
const API_HOST =
  process.env.REACT_APP_API_URL ||
  "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net";

const API_BASE = API_HOST + "/api/v1";
const COMPANY_NAME = "ThriftOps";

function formatMoneyMapInline(moneyMap) {
  const entries = Object.entries(moneyMap || {});
  if (!entries.length) return "—";
  return entries.map(([cur, amt]) => formatMoney(amt, cur)).join("  +  ");
}

function buildReceiptNumber({ vendorId, status, period }) {
  if (period) {
    const { year, month, half } = period;
    return `VP-${vendorId}-${year}${String(month).padStart(2, "0")}-H${half}`;
  }
  const today = new Date();
  const stamp = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, "0")}${String(today.getDate()).padStart(2, "0")}`;
  return `VP-${vendorId}-${status.toUpperCase()}-${stamp}`;
}

export default function VendorPayoutReceiptPage() {
  const { vendorId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, companyId } = useAuth();
  const userRole = normalizeRole(user?.role);
  const isAllowed = userRole === "owner" || userRole === "seller manager";

  const status = searchParams.get("status") || "Delivered";
  const isDeliveredView = status === "Delivered";
  const isCancelledView = status === "Cancelled";
  const year = searchParams.get("year");
  const month = searchParams.get("month");
  const half = searchParams.get("half");
  const [viewMode, setViewMode] = useState("internal"); // "internal" | "vendor"
  const isVendorCopy = viewMode === "vendor";
  const period = useMemo(
    () =>
      isDeliveredView && year && month && half
        ? { year: Number(year), month: Number(month), half: Number(half) }
        : null,
    [isDeliveredView, year, month, half],
  );

  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadReceipt = useCallback(async () => {
    if (!companyId || !isAllowed) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        company_id: companyId,
        role: userRole,
        status,
        vendor_id: vendorId,
      });
      if (period) {
        params.set("year", period.year);
        params.set("month", period.month);
        params.set("half", period.half);
      }
      const res = await fetch(
        `${API_BASE}/orders/dashboard/vendor-payouts?${params.toString()}`,
      );
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError(json.error || "Failed to load receipt");
        return;
      }
      const found = (json.vendors || []).find(
        (v) => String(v.vendor_id) === String(vendorId),
      );
      setVendor(found || null);
      if (!found)
        setError("No payout data found for this vendor in the selected view.");
    } catch (err) {
      setError(err.message || "Failed to load receipt");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, isAllowed, userRole, status, vendorId, year, month, half]);

  useEffect(() => {
    loadReceipt();
  }, [loadReceipt]);

  const receiptNumber = useMemo(
    () => buildReceiptNumber({ vendorId, status, period }),
    [vendorId, status, period],
  );

  const issuedAt = useMemo(
    () =>
      new Date().toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
    [],
  );

  if (!isAllowed) {
    return (
      <MDBContainer fluid className="py-4">
        <div className="vpr-denied">
          This report is restricted to Owner and Seller Manager roles.
        </div>
      </MDBContainer>
    );
  }

  return (
    <div className="vpr-root">
      <div className="vpr-toolbar">
        <button
          type="button"
          className="vpr-back-btn"
          onClick={() => navigate(-1)}
        >
          ← Back to payouts
        </button>
        <div className="vpr-toolbar-right">
          <div className="vpr-view-toggle">
            <button
              type="button"
              className={`vpr-view-toggle-btn ${!isVendorCopy ? "active" : ""}`}
              onClick={() => setViewMode("internal")}
            >
              Internal (with selling price)
            </button>
            <button
              type="button"
              className={`vpr-view-toggle-btn ${isVendorCopy ? "active" : ""}`}
              onClick={() => setViewMode("vendor")}
            >
              Vendor copy (payout only)
            </button>
          </div>
          <button
            type="button"
            className="vpr-print-btn"
            onClick={() => window.print()}
            disabled={loading || !vendor}
          >
            Print / Save as PDF
          </button>
        </div>
      </div>

      {loading ? (
        <div className="vpr-state">
          <MDBSpinner role="status" />
          <span>Loading receipt…</span>
        </div>
      ) : error || !vendor ? (
        <div className="vpr-state vpr-state-error">
          <p>{error || "No data available."}</p>
        </div>
      ) : (
        <div className="vpr-sheet">
          {/* ── Letterhead ─────────────────────────────────── */}
          <div className="vpr-letterhead">
            <div>
              <div className="vpr-company">{COMPANY_NAME}</div>
              <div className="vpr-company-sub">
                Vendor Payout Receipt{" "}
                {isVendorCopy && (
                  <span className="vpr-copy-tag">Vendor Copy</span>
                )}
              </div>
            </div>
            <div className="vpr-letterhead-right">
              <div className="vpr-receipt-title">PAYOUT RECEIPT</div>
              <div className="vpr-receipt-meta">
                Receipt No. {receiptNumber}
              </div>
              <div className="vpr-receipt-meta">Issued {issuedAt}</div>
            </div>
          </div>

          <hr className="vpr-rule" />

          {/* ── Paid to / Period block ─────────────────────── */}
          <div className="vpr-info-grid">
            <div className="vpr-info-block">
              <div className="vpr-info-label">Paid to</div>
              <div className="vpr-info-main">{vendor.vendor_name}</div>
              {vendor.vendor_store && (
                <div className="vpr-info-line">{vendor.vendor_store}</div>
              )}
              {vendor.vendor_phone && (
                <div className="vpr-info-line">{vendor.vendor_phone}</div>
              )}
              <div className="vpr-info-line vpr-muted">
                Vendor ID: {vendor.vendor_id}
              </div>
            </div>
            <div className="vpr-info-block">
              <div className="vpr-info-label">
                {isDeliveredView ? "Payout period" : "Status view"}
              </div>
              <div className="vpr-info-main">
                {isDeliveredView ? periodLabel(period) : status}
              </div>
              <div className="vpr-info-line">
                {isCancelledView ? (
                  <span className="vpr-status-tag vpr-status-negative">
                    Not payable
                  </span>
                ) : isDeliveredView ? (
                  <span className="vpr-status-tag vpr-status-positive">
                    Final
                  </span>
                ) : (
                  <span className="vpr-status-tag vpr-status-neutral">
                    Estimate
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* ── Orders ──────────────────────────────────────── */}
          <div className="vpr-orders">
            {vendor.orders.map((order) => (
              <div key={order.order_id} className="vpr-order">
                <div className="vpr-order-header">
                  <span className="vpr-order-id">
                    Order #{formatDisplayOrderNumber(order.order_id)}
                  </span>
                  <span className="vpr-order-tag">{order.marketplace}</span>
                  <span className="vpr-order-date">
                    {isDeliveredView ? "Delivered" : "Updated"}{" "}
                    {formatDateTime(order.status_changed_at)}
                  </span>
                </div>
                <table className="vpr-items">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>SKU</th>
                      <th className="">Qty</th>
                      {!isVendorCopy && <th className="">Selling price</th>}
                      <th className="">
                        {isVendorCopy ? "Payout" : "Amount payable"}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items.map((item) => (
                      <tr key={item.product_id}>
                        <td>{item.title}</td>
                        <td className="vpr-muted">{item.sku || "—"}</td>
                        <td className="vpr-num">{item.quantity || 1}</td>
                        {!isVendorCopy && (
                          <td className="vpr-num">
                            {formatMoney(item.price, order.currency)}
                          </td>
                        )}
                        <td className="vpr-num">
                          {isCancelledView
                            ? "—"
                            : formatMoney(item.amount_payable, order.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td
                        colSpan={isVendorCopy ? 3 : 4}
                        className="vpr-order-total-label"
                      >
                        Order total
                      </td>
                      <td className="vpr-num vpr-order-total-value">
                        {isCancelledView
                          ? "Not payable"
                          : formatMoney(
                              order.order_total_payout,
                              order.currency,
                            )}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ))}
          </div>

          {/* ── Summary ─────────────────────────────────────── */}
          <div className="vpr-summary">
            <div className="vpr-summary-row">
              <span>Total orders</span>
              <span className="vpr-num">{vendor.total_orders}</span>
            </div>
            <div className="vpr-summary-row">
              <span>Total items</span>
              <span className="vpr-num">{vendor.total_items}</span>
            </div>
            <div className="vpr-summary-row vpr-summary-grand">
              <span>
                {isCancelledView ? "Total (not payable)" : "Total payable"}
              </span>
              <span className="vpr-num">
                {isCancelledView
                  ? "—"
                  : formatMoneyMapInline(vendor.total_payout)}
              </span>
            </div>
          </div>

          {/* ── Footer ──────────────────────────────────────── */}
          <div className="vpr-footer">
            <p>
              {isCancelledView
                ? "These orders were cancelled and are shown for record-keeping only — no payout is due."
                : isDeliveredView
                  ? "This receipt reflects delivered orders for the stated period and is considered final."
                  : "This is a preview of orders not yet delivered. Amounts are estimates and may change before final payout."}
            </p>
            <p className="vpr-footer-meta">
              Generated {issuedAt} · {COMPANY_NAME} — Vendor Payout System
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
